import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

export class MediaGallery extends Component {
  #controller = new AbortController();
  #videoObserver;
  #activePlayer = null;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });

    const slideshow = this.slideshow;
    if (slideshow) {
      this.#videoObserver = new MutationObserver(this.#scheduleVideoUpdate);
      this.#videoObserver.observe(slideshow, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-hidden', 'hidden', 'reveal', 'transitioning', 'disabled'],
      });
      document.addEventListener('visibilitychange', this.#scheduleVideoUpdate, { signal });
      this.#scheduleVideoUpdate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#videoObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#activePlayer?.pauseMedia?.();
    this.#activePlayer = null;
  }

  #scheduleVideoUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateVideo());
  };

  #updateVideo() {
    const slideshow = this.slideshow;
    if (!slideshow || !this.isConnected) return;
    // Only affect the carousel, including the mobile carousel of a grid gallery.
    const slide = slideshow.slides?.[slideshow.current];
    const isVisible = !document.hidden && !slideshow.disabled &&
      !slideshow.hasAttribute('transitioning') &&
      slideshow.getClientRects().length > 0 &&
      slide?.getAttribute('aria-hidden') === 'false';
    const player = isVisible ? slide.querySelector('deferred-media') : null;

    if (player === this.#activePlayer) return;
    this.#activePlayer?.pauseMedia?.();
    this.#activePlayer = null;
    if (!player || typeof player.loadContent !== 'function') return;

    // Leave external videos on their existing manual controls. Provider readiness
    // requires separate handling; this patch autoplays Shopify-hosted videos only.
    const templateVideo = player.querySelector('template')?.content.querySelector('video');
    if (!templateVideo && !player.querySelector('video')) return;

    player.loadContent(false);
    const video = player.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    this.#activePlayer = player;
    const attempt = video.play();
    attempt?.then(() => {
      if (this.#activePlayer !== player || !this.isConnected) {
        video.pause();
        return;
      }
      player.isPlaying = true;
      player.updatePlayPauseHint?.(true);
    }).catch(() => {
      if (this.#activePlayer !== player) return;
      player.isPlaying = false;
      player.updatePlayPauseHint?.(false);
    });
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    this.replaceWith(newMediaGallery);
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
